import cn from 'classnames';
import { NextPage } from 'next';
import { useSession } from 'next-auth/react';
import { NextSeo, NextSeoProps } from 'next-seo';
import React, { useMemo } from 'react';
import { PostManager } from '@data/post/PostManager';
import { ExpectedProps, ServerPropsFactory } from '@lib/factory/serverprops';
import { isPostPersonal } from '@services/posts/helpers';
import { LinksEnum } from '@specs/navigation';
import { useGetIsBlockedBy } from '@hooks/TRPC_Hooks/useGetIsBlockedBy';
import { translateType, useTranslation } from '@hooks/useTranslation';
import { PostCard } from '@components/Cards/PostCard/PostCard';
import { PageLayout } from '@components/PageLayout/PageLayout';
import { Breadcrumb } from '@components/UIKit/Breadcrumb/Breadcrumb';
import { UnavailablePost } from '@components/UIKit/UnavailablePost/UnavailablePost';
import { PostView } from '@data/post/PostManagerSpec';
import { useGetIsBlocked } from '@hooks/TRPC_Hooks/useGetIsBlocked';
import s from '../postPage.module.scss';

// Error Boundary Component
class PostErrorBoundary extends React.Component<{
  children: React.ReactNode;
}, {
  hasError: boolean;
  error?: Error;
}> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Post page error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <PageLayout showBackIconOnMobile data-sentry-element="PageLayout" data-sentry-component="PostErrorBoundary" data-sentry-source-file="index.tsx">
                    <div style={{
          padding: '20px',
          textAlign: 'center'
        }}>
                        <h2>Sorry, something went wrong loading this post.</h2>
                        <p>Please try refreshing the page.</p>
                        {process.env.NODE_ENV === 'development' && <details style={{
            marginTop: '20px',
            textAlign: 'left'
          }}>
                                <summary>Error Details (Development)</summary>
                                <pre style={{
              fontSize: '12px',
              overflow: 'auto'
            }}>{this.state.error?.stack}</pre>
                            </details>}
                    </div>
                </PageLayout>;
    }
    return this.props.children;
  }
}
const getSeoConfig = (post: PostView, tr: translateType): NextSeoProps => {
  const title = tr({
    id: 'post.page.title'
  }, {
    name: post.title || 'Post'
  });
  const description = tr({
    id: 'post.page.description'
  }, {
    name: post.title || 'Post'
  });
  const url = `${process.env.NEXT_PUBLIC_BASE_URL}/posts/${post.id}`;
  return {
    title,
    description,
    canonical: url,
    openGraph: {
      type: 'article',
      url,
      title,
      description,
      images: [{
        url: `${process.env.NEXT_PUBLIC_BASE_URL}/apple-touch-icon.png`,
        width: 1200,
        height: 630,
        alt: post.title || 'Post'
      }],
      site_name: 'RToys App'
    },
    twitter: {
      handle: '@rtoysapp',
      site: '@rtoysapp',
      cardType: 'summary_large_image'
    },
    additionalMetaTags: [{
      name: 'twitter:title',
      content: title
    }, {
      name: 'twitter:description',
      content: description
    }, {
      name: 'twitter:image',
      content: `${process.env.NEXT_PUBLIC_BASE_URL}/apple-touch-icon.png`
    }]
  };
};
export const getServerSideProps = ServerPropsFactory(async (props: ExpectedProps) => {
  const initialPost = props.slug ? await PostManager.getPost(Number(props.slug), props.session?.uid) : await Promise.resolve(null);
  if (!initialPost) {
    return {
      ...props,
      redirect: {
        destination: LinksEnum.ERROR_404,
        permanent: false
      }
    };
  }
  const personalPost = isPostPersonal(props.session?.uid, initialPost.author_id);
  return {
    ...props,
    personalPost,
    initialPost
  } satisfies PageProps;
}, [{
  key: 'initialPost',
  destination: LinksEnum.ERROR_404
}]);
interface PageProps extends ExpectedProps {
  personalPost: boolean;
  initialPost: PostView;
}
const Page: NextPage<PageProps> = ({
  personalPost,
  initialPost
}) => {
  const {
    translate: tr
  } = useTranslation();
  const {
    status
  } = useSession();

  // Call hooks first, before any defensive checks
  const {
    isBlocked: isBlockedByMe
  } = useGetIsBlockedBy({
    targetShortId: initialPost?.creator?.shortid ?? ''
  }, {
    trpc: {
      context: {
        skipBatch: true
      }
    },
    enabled: status === 'authenticated' && !personalPost && !!initialPost?.creator?.shortid,
    staleTime: 0,
    cacheTime: 0
  });
  const {
    isBlocked: isBlockedByOther
  } = useGetIsBlocked({
    targetShortId: initialPost?.creator?.shortid ?? ''
  }, {
    trpc: {
      context: {
        skipBatch: true
      }
    },
    enabled: status === 'authenticated' && !personalPost && !!initialPost?.creator?.shortid,
    staleTime: 0,
    cacheTime: 0
  });
  const isBlocked = isBlockedByMe || isBlockedByOther;

  // Call useMemo after other hooks but before defensive checks
  const seoConfig = useMemo(() => {
    if (!initialPost) return {};
    return getSeoConfig(initialPost, tr);
  }, [initialPost, tr]);

  // Defensive check for initialPost after hooks
  if (!initialPost) {
    return <PostErrorBoundary>
                <PageLayout showBackIconOnMobile>
                    <div>Post not found</div>
                </PageLayout>
            </PostErrorBoundary>;
  }
  return <PostErrorBoundary data-sentry-element="PostErrorBoundary" data-sentry-component="Page" data-sentry-source-file="index.tsx">
            <PageLayout showBackIconOnMobile data-sentry-element="PageLayout" data-sentry-source-file="index.tsx">
                <NextSeo {...seoConfig} data-sentry-element="NextSeo" data-sentry-source-file="index.tsx" />
                <div className={s.postPage}>
                    <div className={cn(s.postPage__container, '_container')}>
                        <Breadcrumb slugTitle={initialPost.title} data-sentry-element="Breadcrumb" data-sentry-source-file="index.tsx" />
                        {/* <div className={s.postPage__top}>
                         </div> */}
                        <div className={s.postPage__box}>
                            {isBlocked ? <UnavailablePost /> : <PostCard isPersonal={personalPost} initialPost={initialPost} />}
                        </div>
                    </div>
                </div>
            </PageLayout>
        </PostErrorBoundary>;
};
export default Page;